import { OPTION_ROW_BASE_CLASS_NAME } from "@/components/ui/optionRow";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@paribus/shared-ui";
import React from "react";

export interface AutocompleteInputViewProps {
  value: string;
  filteredSuggestions: readonly string[];
  /** -1 when no option is active. */
  activeIndex: number;
  isOpen: boolean;
  listboxId: string;
  id?: string;
  placeholder?: string;
  disabled: boolean;
  className?: string;
  ariaLabel?: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  activeOptionRef: React.RefObject<HTMLDivElement | null>;
  onValueChange: (next: string) => void;
  onFocus: () => void;
  onClick: () => void;
  onBlur: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onOptionPick: (option: string) => void;
  onOptionHover: (index: number) => void;
  onOpenChange: (nextOpen: boolean) => void;
}

export const AutocompleteInputView = ({
  value,
  filteredSuggestions,
  activeIndex,
  isOpen,
  listboxId,
  id,
  placeholder,
  disabled,
  className,
  ariaLabel,
  inputRef,
  activeOptionRef,
  onValueChange,
  onFocus,
  onClick,
  onBlur,
  onKeyDown,
  onOptionPick,
  onOptionHover,
  onOpenChange,
}: AutocompleteInputViewProps) => {
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
          }
          aria-label={ariaLabel}
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          onChange={(event) => onValueChange(event.target.value)}
          onFocus={onFocus}
          onClick={onClick}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
        />
      </PopoverAnchor>
      <PopoverContent
        className="max-h-[300px] w-[--radix-popover-trigger-width] overflow-y-auto p-1"
        align="start"
        // Focus must stay in the input, so suppress Radix's focus moves on open/close.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        // Mousedown inside this content (padding, scrollbar, rows) must not move focus off the
        // input — a stray blur would close the list before a click on an option can land.
        // stopPropagation keeps the mousedown from reaching document-level outside-click listeners
        // (see docs/guidelines/client/components.md "Portals vs hand-rolled outside-click listeners").
        // Options themselves commit on click, not mousedown, so this preventDefault does not
        // suppress the option's own click — only the browser's default focus/selection behavior.
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        // Radix treats the anchor as "outside" the content and only exempts a PopoverTrigger, via
        // a `triggerRef` that PopoverAnchor never populates (as of @radix-ui/react-popover 1.1.15).
        // So any focusin on the anchor while the list is open dismisses it (onFocusOutside), and
        // the pointerdown that starts a click on the anchor does too (onPointerDownOutside) —
        // exempt the anchor explicitly from both, the same way Radix exempts its own trigger.
        // Browser-only: jsdom's pointer events never reach Radix's document listener, so this
        // guard has no test, unlike its onFocusOutside twin below.
        onPointerDownOutside={(event) => {
          if (event.target === inputRef.current) {
            event.preventDefault();
          }
        }}
        onFocusOutside={(event) => {
          if (event.target === inputRef.current) {
            event.preventDefault();
          }
        }}
      >
        <div id={listboxId} role="listbox" onMouseLeave={() => onOptionHover(-1)}>
          {filteredSuggestions.map((option, index) => (
            <div
              key={`${listboxId}-option-${index}`}
              id={`${listboxId}-option-${index}`}
              ref={index === activeIndex ? activeOptionRef : undefined}
              role="option"
              aria-selected={index === activeIndex}
              className={cn(
                OPTION_ROW_BASE_CLASS_NAME,
                index === activeIndex && "bg-accent text-accent-foreground"
              )}
              onClick={() => onOptionPick(option)}
              onMouseEnter={() => onOptionHover(index)}
            >
              <span className="flex-1 truncate">{option}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
